import * as Sentry from '@sentry/react';
import { format, parseISO } from 'date-fns';
import { graphql, useStaticQuery } from 'gatsby';
import { t } from 'i18next';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { ALL_LANGS } from '../../../constants';
import { Experience } from '../../../templates/ExperiencePage';
import i18n from '../../../translation/i18n';
import { mapBy, uniqBy } from '../../../utils/nodash';
import { localize } from '../../../utils/sanity';
import { clsx } from '../../../utils/utils';
import bookingKitClient, {
  BookingKitDate,
  EventDetailsWithDates,
} from '../../../vendorApis/bookingkit';
import { CommonModuleProps } from '../../ModulesContent';
import Button from '../Button';
import { NewsletterBlock } from '../Footer';

// Calendar pulls in react-day-picker (~9.7 KB gzipped) and is only rendered
// once the user opens the date picker. Lazy so it doesn't ship in the app
// bundle on first paint.
const Calendar = React.lazy(() =>
  import('../Calendar').then(m => ({ default: m.Calendar })),
);
import Form from '../Form';
import ModuleLayout from '../ModuleLayout';
import ExperiencesModule from './ExperiencesModule';
import * as styles from './ExperiencesBookingModule.module.scss';

// Types previously in ExperiencesPage.tsx, now shared from here
export type APIExperience = EventDetailsWithDates;
export type CMSPlusAPIExperience = Experience & {
  price: string;
  dates: Array<BookingKitDate>;
};
export type DateWithId = {
  date: Date;
  id: string;
};

export const ExperiencesBookingModuleFragment = graphql`
  fragment ExperiencesBookingModule on SanityExperiencesBookingModule {
    experiencesToUse
    chosenExperiences: experiences {
      title {
        ...LocaleStringFragment
      }
      slug {
        current
      }
      bookingKitId
      theme
      image {
        ...SanityImage
      }
      description {
        ...LocaleTextFragment
      }
    }
    showDatePicker
    showWaitingList
    buttonText {
      ...LocaleStringFragment
    }
  }
`;

function formatDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export type ExperiencesBookingModuleProps = {
  experiencesToUse?: 'all' | 'chooseManually';
  chosenExperiences?: Array<Experience>;
  showDatePicker?: boolean;
  showWaitingList?: boolean;
  buttonText?: string;
  className?: string;
};

interface LocalizedStaticQueryData {
  allSanityExperience: {
    nodes: Array<Experience>;
  };
  sanityWebsite: {
    footer: {
      newsletterBlock?: NewsletterBlock;
    };
  };
}

function ExperiencesBookingModule(
  props: ExperiencesBookingModuleProps & CommonModuleProps,
): React.ReactElement {
  const staticData = useStaticQuery<LocalizedStaticQueryData>(graphql`
    {
      allSanityExperience(sort: { orderRank: ASC }) {
        nodes {
          ...Experience
        }
      }
      sanityWebsite {
        footer {
          newsletterBlock {
            title {
              ...LocaleStringFragment
            }
            buttonText {
              ...LocaleStringFragment
            }
          }
        }
      }
    }
  `);

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedStaticQueryData = localize(staticData, langs);

  const {
    chosenExperiences,
    experiencesToUse = 'all',
    showDatePicker = true,
    showWaitingList = true,
    buttonText,
    className,
    moduleId,
    theme,
  } = props;

  function mergeCMSExperienceWithAPI(
    cmsExperience: Experience,
    apiExperience: APIExperience,
  ): CMSPlusAPIExperience {
    const price =
      apiExperience.prices.find(price => price.title === 'Standard') ??
      apiExperience.prices[0];
    return {
      ...cmsExperience,
      price: price?.value ?? '',
      dates: apiExperience.dates,
    };
  }

  const experiencesToShow =
    experiencesToUse === 'chooseManually' && chosenExperiences
      ? chosenExperiences
      : localizedData.allSanityExperience.nodes;

  const [apiExperiences, setAPIExperiences] = useState<Array<CMSPlusAPIExperience> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);

  const today = useMemo(() => new Date(), []);

  const { firstAvailableDate, lastAvailableDate, availableDatesStrSet } = useMemo(() => {
    const apiDates =
      apiExperiences
        ?.flatMap(experience => experience.dates)
        .map(apiDate => new Date(apiDate.date)) || [];

    let firstAvailableDate: Date | null = null;
    let lastAvailableDate: Date | null = null;
    for (const apiDate of apiDates) {
      if (!firstAvailableDate || apiDate.getTime() < firstAvailableDate.getTime()) {
        firstAvailableDate = apiDate;
      }
      if (!lastAvailableDate || apiDate.getTime() > lastAvailableDate.getTime()) {
        lastAvailableDate = apiDate;
      }
    }

    const availableDatesStrSet = new Set(apiDates.map(formatDate));

    return {
      firstAvailableDate,
      lastAvailableDate,
      availableDatesStrSet,
    };
  }, [apiExperiences === null]);

  useEffect(() => {
    void (async () => {
      try {
        // If API takes too long to answer, show experiences anyway
        setTimeout(() => {
          setLoading(false);
        }, 1500);

        const fetchedExperiences = await Promise.all(
          experiencesToShow
            .map(experience => experience.bookingKitId)
            .map(id => bookingKitClient.getEventDetails(id, { withDates: true })),
        );
        const fetchedExperiencesById = mapBy(uniqBy(fetchedExperiences, 'id'), 'id');
        setAPIExperiences(
          experiencesToShow.map(cmsExperience =>
            mergeCMSExperienceWithAPI(
              cmsExperience,
              fetchedExperiencesById[cmsExperience.bookingKitId]!,
            ),
          ),
        );
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (apiExperiences) {
      // Sync state with URL on component mount
      const params = new URLSearchParams(window.location.search);
      const dateStr = params.get('date');
      if (dateStr) {
        const date = parseISO(dateStr);
        if (availableDatesStrSet.has(formatDate(date))) {
          setSelectedDate(date);
        } else {
          params.delete('date');
          const query = params.toString();
          window.history.replaceState(
            {},
            '',
            window.location.pathname + (query ? `?${query}` : ''),
          );
        }
      }
    }
  }, [apiExperiences === null]);

  useEffect(() => {
    if (apiExperiences) {
      // Sync URL with state when selectedDate changes
      const params = new URLSearchParams(window.location.search);
      if (selectedDate) {
        params.set('date', formatDate(selectedDate));
      } else {
        params.delete('date');
      }
      const query = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (query ? `?${query}` : ''),
      );
    }
  }, [apiExperiences === null, selectedDate?.getTime()]);

  const handleDateSelect = (date?: Date) => {
    if (!date) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
    setIsDatePickerOpen(false);
  };

  const showWaitingListForm = showWaitingList && apiExperiences && !firstAvailableDate;

  return (
    <ModuleLayout
      id={moduleId}
      className={className}
      currentModuleBgColor={theme}
      contentClassName={styles.contentContainer}
    >
      {showDatePicker && (
        <div
          className={clsx(
            styles.datePickerContainer,
            !firstAvailableDate && styles.datePickerContainerHidden,
          )}
        >
          <Button
            className={styles.datePickerButton}
            onClick={() => {
              setIsDatePickerOpen(!isDatePickerOpen);
            }}
          >
            {selectedDate
              ? t('calendar_change_date', 'Change date ({{selectedDate}})', {
                  selectedDate: format(selectedDate, 'MMM d'),
                })
              : t('calendar_select_date', 'Select date')}
          </Button>
          {isDatePickerOpen && firstAvailableDate && (
            <Suspense fallback={null}>
            <Calendar
              fromYear={today.getFullYear()}
              toYear={today.getFullYear()}
              fromMonth={
                today.getTime() < firstAvailableDate.getTime() ? today : firstAvailableDate
              }
              toMonth={lastAvailableDate || undefined}
              defaultMonth={selectedDate || undefined}
              disabled={date => !availableDatesStrSet.has(formatDate(date))}
              selected={selectedDate || undefined}
              onSelect={handleDateSelect}
            ></Calendar>
            </Suspense>
          )}
        </div>
      )}
      {showWaitingListForm && (
        <div className={styles.waitingListContainer}>
          <Form
            title={t('unavailable_dates.title', 'Join our waiting list')}
            text={
              <div className={styles.waitingListTextContainer}>
                <p className={styles.waitingListSubtitle}>
                  {t(
                    'unavailable_dates.subtitle',
                    'Tickets for this season will be available soon!',
                  )}
                </p>
                <p className={styles.waitingListText}>
                  {t(
                    'unavailable_dates.text',
                    'But we really want you to be able to enjoy Dinner In The Sky. Please subscribe to our newsletter to be the first to know when tickets are available.',
                  )}
                </p>
              </div>
            }
            labelBeforeInput={
              <p className={styles.waitingListNewsletterTitle}>
                {localizedData.sanityWebsite.footer.newsletterBlock?.title}
              </p>
            }
            inputClassName={styles.newsletterInput}
            withSubmitButtonInsideInput
            submitButtonText={localizedData.sanityWebsite.footer.newsletterBlock?.buttonText}
            thankYouScreen={{
              title: t('waiting_list.thank_you_title', 'Thank you!'),
              text: t(
                'waiting_list.thank_you_text',
                "You've successfully joined our waiting list. We'll notify you as soon as new tickets are available for the upcoming season. Stay tuned!",
              ),
            }}
            hideLabels
            fields={[
              {
                crmFieldId: 'email',
                id: 'email',
                fieldType: 'textSingleLine',
                title: 'Email',
                withEmailValidation: true,
              },
            ]}
          />
        </div>
      )}
      <ExperiencesModule
        experiences={apiExperiences || experiencesToShow}
        selectedDate={selectedDate}
        loading={loading}
        buttonText={buttonText}
      />
    </ModuleLayout>
  );
}

export default ExperiencesBookingModule;
